import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  StudentProgressRecitationBook,
  StudentProgressRecitationLevel,
} from '../../../constants/types/student/homework/type';
import TextWinds from '../../common/TextWinds';
import { Tooltip } from 'antd';
import {
  CookieKey,
  CookieTypeJebsAiRecitationResult,
  CookieTypeUserInfo,
  createCookie,
} from '../../../util/cookie';
import openPopupWindow from '../../../util/popupWindow';

interface LevelProgressCardProps {
  level: StudentProgressRecitationLevel;
  isOpened: boolean;
  onOpenToggle: () => void;
}

type LevelProgressStatus = 'PASSED' | 'FAILED' | 'ATTEMPTED' | 'NONE';

const CELL_CLASS_MAP: Record<LevelProgressStatus, string> = {
  PASSED: 'border-green-200 bg-green-100 text-green-700',
  FAILED: 'border-red-200 bg-red-100 text-red-700',
  ATTEMPTED: 'border-blue-200 bg-blue-100 text-[#0B8ED0]',
  NONE: 'border-gray3 bg-gray0 text-gray7',
};

const getBookNumberText = (bookName: string) => {
  const matchedNumber = bookName.match(/\d+/)?.[0];

  return matchedNumber ?? bookName;
};

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 젭스온(AI인증제) [진도 지도] > Level 카드
 */
const LevelProgressCard = ({
  level,
  isOpened,
  onOpenToggle,
}: LevelProgressCardProps) => {
  const { getCookie: getUserInfoCookie } = createCookie<CookieTypeUserInfo>(
    CookieKey.USER_INFO,
  );

  const {
    removeCookie: removeJebsAiRecitationResultCookie,
    setCookie: setJebsAiRecitationResultCookie,
  } = createCookie<CookieTypeJebsAiRecitationResult>(
    CookieKey.JEBS_AI_RECITATION_RESULT,
  );

  const handleShowTestResult = (book: StudentProgressRecitationBook) => {
    // 기존 AI인증제 결과 쿠키 삭제
    try {
      removeJebsAiRecitationResultCookie();
    } catch (error) {
      // 쿠키가 없는 경우 무시
      // console.error(error);
    }

    const { studentId, uid } = getUserInfoCookie();
    setJebsAiRecitationResultCookie({
      student: {
        id: studentId,
        uid: uid,
      },
      recitation: {
        id: book.todoId,
        level: String(level.level),
        book: String(book.book),
        result: book.status === 'PASSED' ? 'PASS' : 'FAIL',
      },
    });

    openPopupWindow(
      `${process.env.REACT_APP_JEBS_APP_URL}/student/jebs-ai-recitation/result`,
      1440,
      1440 / 840,
    );
  };
  return (
    <article className="overflow-hidden rounded-xl border border-gray3 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        onClick={onOpenToggle}
      >
        <div className="flex min-w-0 items-center gap-4">
          <TextWinds
            type="content_body2"
            color="gray8"
            className="w-[90px] shrink-0 font-bold"
          >
            {`LEVEL ${level.level}`}
          </TextWinds>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {isOpened ? (
            <ChevronUpIcon className="h-5 w-5 text-gray6" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray6" />
          )}
        </div>
      </button>

      {isOpened && (
        <div className="border-t border-gray3 px-4 py-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {level.books.map((book, index) => {
                const bookName = book.bookName;

                const canOpenLesson =
                  book.status === 'PASSED' || book.status === 'FAILED';

                const tooltipTitle = canOpenLesson ? (
                  <div className="flex flex-col gap-1">
                    <span>{bookName}</span>
                    <span className="text-center text-xs opacity-80">
                      클릭하여 결과 확인
                    </span>
                  </div>
                ) : (
                  bookName
                );

                return (
                  <Tooltip
                    key={index}
                    title={tooltipTitle}
                    placement="top"
                    zIndex={10002}
                  >
                    {canOpenLesson ? (
                      <button
                        type="button"
                        className={[
                          'flex h-9 w-9 items-center justify-center rounded-lg border text-sm font-bold',
                          'transition-transform hover:scale-105 hover:shadow-sm active:scale-95',
                          'cursor-pointer',
                          CELL_CLASS_MAP[book.status as LevelProgressStatus],
                        ].join(' ')}
                        onClick={() => handleShowTestResult(book)}
                      >
                        {getBookNumberText(book.bookName)}
                      </button>
                    ) : (
                      <span
                        className={[
                          'flex h-9 w-9 cursor-default items-center justify-center rounded-lg border text-sm font-bold',
                          CELL_CLASS_MAP[book.status as LevelProgressStatus],
                        ].join(' ')}
                      >
                        {getBookNumberText(book.bookName)}
                      </span>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default LevelProgressCard;
